'use client';

import { ChangeEvent, useContext, useState } from 'react';
import KubernetesAppName from '~/constants/common/KubernetesAppName';
import CustomConfigs from '~/constants/customConfigs';
import StaticContext from '~/context/common/StaticContext';
import BrowserCookies from '~/utilities/BrowserCookies';
import { getBranchCookieName } from '~/utilities/featureBranch';
import FormField from '../FormField';
import {
  Container,
  Heading,
  Input,
  SmallSecondaryButton,
  Text,
  TextStrong
} from '../PassengerTools.styles';
import { copyToClipboard } from '../utils/createTestUser';

const BRANCH_COOKIE_PREFIX = 'carvana-merch-merchui-';
const BRANCH_REGEX = /reo-[0-9a-z-]*/;
const SPECIFIC_KEY = 'specific';

type AppOption = { label: string; prefix: string };

const APP_ROWS: AppOption[] = [
  { label: 'All Apps', prefix: 'all' },
  { label: 'Homepage', prefix: KubernetesAppName.Homepage },
  { label: 'SRP', prefix: KubernetesAppName.SRP },
  { label: 'VDP', prefix: KubernetesAppName.VDP },
  { label: 'Dealership', prefix: KubernetesAppName.Dealership },
  { label: 'Merch UI', prefix: KubernetesAppName.MerchUI }
];

const SPECIFIC_APP_OPTIONS: AppOption[] = [
  { label: 'Homepage', prefix: KubernetesAppName.Homepage },
  { label: 'SRP', prefix: KubernetesAppName.SRP },
  { label: 'VDP', prefix: KubernetesAppName.VDP },
  { label: 'Dealership', prefix: KubernetesAppName.Dealership },
  { label: 'MerchUI', prefix: KubernetesAppName.MerchUI }
];

export default function ShareLink() {
  const { kubernetesAppName } = useContext(StaticContext);

  const [branchInput, setBranchInput] = useState(() => {
    const cookieValue =
      BrowserCookies.get(getBranchCookieName(kubernetesAppName)) ?? '';

    return cookieValue.startsWith(BRANCH_COOKIE_PREFIX)
      ? cookieValue.slice(BRANCH_COOKIE_PREFIX.length)
      : '';
  });
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [specificAppsEnabled, setSpecificAppsEnabled] = useState(false);
  const [specificApps, setSpecificApps] = useState<string[]>([]);

  const parsedBranch = branchInput.toLowerCase().match(BRANCH_REGEX)?.[0] ?? '';
  const isValid = parsedBranch.length > 0;
  const showError = branchInput.length > 0 && !isValid;

  const buildUrl = (prefix: string) =>
    `${window.location.origin}/?${CustomConfigs.FeatureBranch}=${prefix}:${parsedBranch}`;

  const handleCopy = (key: string, prefix: string) => {
    copyToClipboard(buildUrl(prefix));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const toggleSpecificApp = (app: string) => {
    setSpecificApps(prev =>
      prev.includes(app) ? prev.filter(a => a !== app) : [...prev, app]
    );
  };

  const specificAppsCsv = SPECIFIC_APP_OPTIONS.filter(({ prefix }) =>
    specificApps.includes(prefix)
  )
    .map(({ prefix }) => prefix)
    .join(',');
  const showSpecificLink =
    isValid && specificAppsEnabled && specificAppsCsv.length > 0;

  return (
    <Container>
      <Heading>Generate Share Link</Heading>
      <FormField label="Branch Name" htmlFor="share-branch-name">
        <Input
          autoCorrect="off"
          id="share-branch-name"
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setBranchInput(e.target.value)
          }
          placeholder="reo-12345"
          type="text"
          value={branchInput}
        />
      </FormField>
      {showError && (
        <Text className="text-red-400">
          Could not parse a branch name — must match reo-12345 format.
        </Text>
      )}
      <div className="flex flex-col gap-md">
        {APP_ROWS.map(({ label, prefix }) => {
          const url = isValid ? buildUrl(prefix) : null;

          return (
            <div key={prefix} className="flex items-center gap-sm">
              <TextStrong className="w-[80px] shrink-0">{label}</TextStrong>
              {url ? (
                <>
                  <a
                    className="flex-1 truncate font-mono text-blue-400 underline t-body-xs"
                    data-analytics-id={null}
                    data-testid={`share-link-${prefix}`}
                    href={url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {url}
                  </a>
                  <SmallSecondaryButton
                    data-analytics-id={null}
                    data-testid={`copy-share-link-${prefix}`}
                    onClick={() => handleCopy(prefix, prefix)}
                  >
                    {copiedKey === prefix ? 'Copied!' : 'Copy'}
                  </SmallSecondaryButton>
                </>
              ) : (
                <Text className="flex-1 opacity-40">—</Text>
              )}
            </div>
          );
        })}

        <label className="mt-md flex items-center gap-sm text-white">
          <input
            checked={specificAppsEnabled}
            data-analytics-id={null}
            data-testid="specific-apps-toggle"
            onChange={e => setSpecificAppsEnabled(e.target.checked)}
            type="checkbox"
          />
          <span>Specific Apps</span>
        </label>

        {specificAppsEnabled && (
          <div className="flex flex-col gap-sm pl-lg">
            {SPECIFIC_APP_OPTIONS.map(({ label, prefix }) => (
              <label
                key={prefix}
                className="flex items-center gap-sm text-white"
              >
                <input
                  checked={specificApps.includes(prefix)}
                  data-analytics-id={null}
                  data-testid={`specific-app-${prefix}`}
                  onChange={() => toggleSpecificApp(prefix)}
                  type="checkbox"
                />
                <span>{label}</span>
              </label>
            ))}

            <div className="mt-sm flex items-center gap-sm">
              {showSpecificLink ? (
                <>
                  <a
                    className="flex-1 truncate font-mono text-blue-400 underline t-body-xs"
                    data-analytics-id={null}
                    data-testid="share-link-specific"
                    href={buildUrl(specificAppsCsv)}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {buildUrl(specificAppsCsv)}
                  </a>
                  <SmallSecondaryButton
                    data-analytics-id={null}
                    data-testid="copy-share-link-specific"
                    onClick={() => handleCopy(SPECIFIC_KEY, specificAppsCsv)}
                  >
                    {copiedKey === SPECIFIC_KEY ? 'Copied!' : 'Copy'}
                  </SmallSecondaryButton>
                </>
              ) : (
                <Text className="flex-1 opacity-40">
                  {isValid
                    ? 'Select at least one app'
                    : 'Enter a branch name above'}
                </Text>
              )}
            </div>
          </div>
        )}
      </div>
    </Container>
  );
}
